import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { ArrowLeft, Spinner, Trash } from '@phosphor-icons/react';
import ManualResultForm from './components/ManualResultForm';
import UploadPanel from './components/UploadPanel';
import DeletedVideosDrawer from './components/DeletedVideosDrawer';
import ConfirmReuploadModal from './components/ConfirmReuploadModal';
import RosterSection from './components/RosterSection';
import ProcessingProgressBar from './components/ProcessingProgressBar';
import HighlightReelsPanel from './components/HighlightReelsPanel';
import { requestPushPermission, showLocalNotification } from '../utils/push';

const MatchDetail = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showImportTeam, setShowImportTeam] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [importingTeam, setImportingTeam] = useState(false);
  const [lastTeam, setLastTeam] = useState(null);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [playerForm, setPlayerForm] = useState({ name: '', number: '', position: '', team: '', team_id: '' });
  const [csvData, setCsvData] = useState('');
  const [csvTeam, setCsvTeam] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [videoMeta, setVideoMeta] = useState(null);
  const [confirmReupload, setConfirmReupload] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletedVideos, setDeletedVideos] = useState([]);
  const [showDeletedDrawer, setShowDeletedDrawer] = useState(false);
  const [notifyOnComplete, setNotifyOnComplete] = useState(false);

  const handleToggleNotify = async () => {
    if (notifyOnComplete) {
      setNotifyOnComplete(false);
      return;
    }
    const result = await requestPushPermission();
    if (result.granted) {
      setNotifyOnComplete(true);
    } else {
      alert(result.reason || 'Notifications were blocked by your browser.');
    }
  };

  const fireUploadCompleteNotification = (videoId) => {
    if (!notifyOnComplete || !match) return;
    const teams = `${match.team_home} vs ${match.team_away}`;
    showLocalNotification('Upload complete', {
      body: `${teams} is ready — AI analysis is queued. Tap to view.`,
      url: `/video/${videoId}`,
      tag: `upload-done-${videoId}`,
    });
  };

  const fetchMatch = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/matches/${matchId}`, { headers: getAuthHeader() });
      setMatch(response.data);
    } catch (err) {
      console.error('Failed to fetch match:', err);
    }
  }, [matchId]);

  const fetchPlayers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/players/match/${matchId}`, { headers: getAuthHeader() });
      setPlayers(response.data);
    } catch (err) {
      console.error('Failed to fetch players:', err);
    }
  }, [matchId]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/teams`, { headers: getAuthHeader() });
      setTeams(response.data);
    } catch (err) { console.error('Failed to fetch teams:', err); }
  }, []);

  const fetchLastTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/last-imported-team`, { headers: getAuthHeader() });
      setLastTeam(res.data?.team_id ? res.data : null);
    } catch (err) { /* non-critical — pill just won't show */ }
  }, []);

  const fetchPendingUploads = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/matches/${matchId}/pending-uploads`, { headers: getAuthHeader() });
      setPendingUploads(res.data?.sessions || []);
    } catch (err) { /* non-critical — banner just won't show */ }
  }, [matchId]);

  useEffect(() => {
    fetchMatch();
    fetchPlayers();
    fetchTeams();
    fetchLastTeam();
    fetchPendingUploads();
  }, [fetchMatch, fetchPlayers, fetchTeams, fetchLastTeam, fetchPendingUploads]);

  useEffect(() => {
    if (!match?.video_id) { setVideoMeta(null); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/videos/${match.video_id}/processing-status`, { headers: getAuthHeader() });
        if (!cancelled) setVideoMeta(res.data);
      } catch { /* video may not exist yet */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [match?.video_id]);

  const fetchDeletedVideos = async () => {
    try {
      const res = await axios.get(`${API}/matches/${matchId}/deleted-videos`, { headers: getAuthHeader() });
      setDeletedVideos(res.data);
    } catch (err) { /* ignore */ }
  };

  const handleRestoreVideo = async (videoId) => {
    if (!window.confirm('Restore this video? Note: any clips, AI markers, and analyses created before deletion are gone — only the video file is recoverable.')) return;
    try {
      await axios.post(`${API}/videos/${videoId}/restore`, {}, { headers: getAuthHeader() });
      setShowDeletedDrawer(false);
      setDeletedVideos([]);
      await fetchMatch();
    } catch (err) {
      alert('Restore failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteVideo = async () => {
    if (!match?.video_id) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/videos/${match.video_id}`, { headers: getAuthHeader() });
      setConfirmReupload(false);
      setVideoMeta(null);
      await fetchMatch();
    } catch (err) {
      alert('Failed to delete video: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        match_id: matchId,
        name: playerForm.name,
        number: playerForm.number !== '' ? parseInt(playerForm.number) : null,
        position: playerForm.position,
        team: playerForm.team || match?.team_home || ''
      };
      if (playerForm.team_id) payload.team_id = playerForm.team_id;
      await axios.post(`${API}/players`, payload, { headers: getAuthHeader() });
      setPlayerForm({ name: '', number: '', position: '', team: '', team_id: '' });
      setShowAddPlayer(false);
      fetchPlayers();
    } catch (err) {
      console.error('Failed to add player:', err);
    }
  };

  const handleCsvImport = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/players/import-csv`, {
        match_id: matchId, csv_data: csvData, team: csvTeam
      }, { headers: getAuthHeader() });
      setCsvData('');
      setCsvTeam('');
      setShowCsvImport(false);
      fetchPlayers();
      alert(`Imported ${res.data.imported} players successfully.`);
    } catch (err) {
      console.error('CSV import failed:', err);
      alert('CSV import failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleImportTeam = async (e) => {
    e.preventDefault();
    if (!selectedTeamId) return;
    await runImportTeam(selectedTeamId);
  };

  const runImportTeam = async (teamId) => {
    if (!teamId) return;
    setImportingTeam(true);
    try {
      const res = await axios.post(
        `${API}/matches/${matchId}/import-team-roster`,
        { team_id: teamId },
        { headers: getAuthHeader() }
      );
      const { imported, skipped, team_name } = res.data;
      setSelectedTeamId('');
      setShowImportTeam(false);
      await fetchPlayers();
      await fetchLastTeam();
      const skippedNote = skipped > 0 ? ` (${skipped} skipped — already on this match)` : '';
      alert(`Imported ${imported} player${imported === 1 ? '' : 's'} from ${team_name}${skippedNote}.`);
    } catch (err) {
      console.error('Team roster import failed:', err);
      alert('Team roster import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImportingTeam(false);
    }
  };

  const handleQuickAttach = async () => {
    if (!lastTeam?.team_id) return;
    await runImportTeam(lastTeam.team_id);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvData(ev.target.result);
    reader.readAsText(file);
  };

  const handleDeletePlayer = async (playerId) => {
    try {
      await axios.delete(`${API}/players/${playerId}`, { headers: getAuthHeader() });
      setPlayers(players.filter(p => p.id !== playerId));
    } catch (err) {
      console.error('Failed to delete player:', err);
    }
  };

  // iter80: handleStandardUpload was removed — every video now goes through
  // the resilient chunked path, even small files. See onFileChange below.

  // Wait until the browser reports the network is back online. Falls back to
  // a 5-second poll if the `online` event somehow doesn't fire (some VPNs or
  // captive portals miss it). Returns immediately if already online.
  const waitForOnline = async (statusSetter) => {
    if (navigator.onLine) return;
    statusSetter('Offline — upload paused. Will auto-resume when connection returns.');
    await new Promise((resolve) => {
      const onOnline = () => {
        window.removeEventListener('online', onOnline);
        clearInterval(poll);
        resolve();
      };
      window.addEventListener('online', onOnline);
      const poll = setInterval(() => {
        if (navigator.onLine) onOnline();
      }, 5000);
    });
  };

  // iter82: 20 retries total to ride out longer object-storage outages.
  //
  // Real production bug 2026-05-21: object storage was returning 500s for
  // ~6 minutes. iter80's 503-on-filesystem-fallback correctly refused to
  // commit ephemeral chunks, but the iter79 client only had 6 retries
  // (~2 min total), so the user's upload aborted at 0 of 85 chunks with
  // a generic alert. Bumping the retry budget to 20 gives transient storage
  // hiccups up to ~15 minutes to clear before we ask the user to resume
  // manually. Most outages clear within 5-10 minutes.
  //
  // Also: differentiate 503 (storage degraded — friendly "paused" message)
  // from 500 ("Chunk failed") so the user understands the wait isn't a bug.
  const uploadChunkWithRetry = async (url, formData, maxRetries = 20, statusSetter = null) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // If the browser thinks we're offline, wait it out BEFORE burning a
        // retry attempt — being offline isn't a chunk failure, it's a pause.
        if (statusSetter) await waitForOnline(statusSetter);
        return await axios.post(url, formData, { headers: getAuthHeader(), timeout: 300000 });
      } catch (err) {
        const status = err.response?.status;
        const isRetryable = !err.response || status >= 500 || err.code === 'ECONNABORTED';
        if (attempt === maxRetries || !isRetryable) throw err;
        const backoffMs = Math.min(2000 * Math.pow(2, attempt - 1), 60000);
        const secs = Math.round(backoffMs / 1000);
        if (statusSetter) {
          if (status === 503) {
            statusSetter(`Storage temporarily slow — auto-resuming in ${secs}s (attempt ${attempt}/${maxRetries}). Keep this tab open.`);
          } else {
            statusSetter(`Chunk failed (attempt ${attempt}/${maxRetries}) — retrying in ${secs}s...`);
          }
        }
        await new Promise(r => setTimeout(r, backoffMs));
      }
    }
  };

  const handleChunkedUpload = async (file) => {
    setUploading(true);
    setUploadStatus('Checking object storage…');
    // iter90 — pre-flight storage probe. Real production outage 2026-05-23
    // had Emergent's object storage backend returning 500 on every PUT for
    // >1h. Pre-iter90 we'd init the upload anyway, start the chunk loop,
    // and burn the full ~15-minute client retry budget before alerting the
    // user. Now we probe first and fail FAST with a friendly modal so the
    // user can come back later instead of wasting 15 minutes.
    try {
      const probe = await axios.get(`${API}/health/storage`, { timeout: 15000 });
      if (probe.data && probe.data.healthy === false) {
        setUploading(false);
        setUploadStatus('');
        alert(
          `We can't start your upload right now — object storage is degraded.\n\n` +
          `Reason: ${probe.data.reason || 'unknown'} (latency ${probe.data.latency_ms || '?'}ms)\n\n` +
          `What to do:\n` +
          `1. Wait 15-30 minutes and try again. Most platform outages clear quickly.\n` +
          `2. If this persists for more than an hour, email support@emergent.sh ` +
          `with subject "Object storage 500 errors" and your app domain.\n\n` +
          `Your file selection has been preserved — just click the file picker again when ready.`
        );
        return;
      }
    } catch (probeErr) {
      // Probe network error itself — assume storage is reachable from backend
      // even if our client can't hit /api/health/storage. Don't block.
      console.warn('Storage probe failed (continuing optimistically):', probeErr);
    }
    setUploadStatus('Initializing upload...');
    try {
      const initResponse = await axios.post(`${API}/videos/upload/init`, {
        match_id: matchId, filename: file.name, file_size: file.size, content_type: file.type || 'video/mp4'
      }, { headers: getAuthHeader(), timeout: 30000 });

      const { upload_id, video_id, chunk_size, resume, uploaded_chunks } = initResponse.data;
      const totalChunks = Math.ceil(file.size / chunk_size);
      const uploadedSet = new Set(uploaded_chunks || []);
      const chunksToUpload = [];
      for (let i = 0; i < totalChunks; i++) {
        if (!uploadedSet.has(i)) chunksToUpload.push(i);
      }
      let uploadedCount = totalChunks - chunksToUpload.length;

      if (resume && uploadedCount > 0) {
        const pct = Math.round((uploadedCount / totalChunks) * 100);
        setUploadProgress(pct);
        setUploadStatus(`Resuming: ${uploadedCount}/${totalChunks} chunks done — ${chunksToUpload.length} remaining`);
      }

      for (const i of chunksToUpload) {
        const start = i * chunk_size;
        const chunk = file.slice(start, Math.min(start + chunk_size, file.size));
        const chunkFormData = new FormData();
        chunkFormData.append('file', chunk);
        const resp = await uploadChunkWithRetry(
          `${API}/videos/upload/chunk?upload_id=${upload_id}&chunk_index=${i}&total_chunks=${totalChunks}`,
          chunkFormData,
          20,
          setUploadStatus,
        );
        uploadedCount++;
        setUploadProgress(Math.round((uploadedCount / totalChunks) * 100));
        setUploadStatus(`Uploading: ${uploadedCount}/${totalChunks} chunks`);
        if (resp.data.status === 'completed') break;
      }
      fireUploadCompleteNotification(video_id);
      navigate(`/video/${video_id}`);
    } catch (err) {
      console.error('Chunked upload failed:', err);
      const fileSizeGB = (file.size / (1024 ** 3)).toFixed(2);
      const status = err.response?.status;
      const reason = err.response?.data?.detail || err.message || 'connection dropped';
      // iter82: refresh the "Incomplete upload waiting" banner immediately so
      // the user sees the resume option without having to reload the page.
      try { await fetchPendingUploads(); } catch (_) { /* non-critical */ }
      // For multi-GB files that exhausted the retry budget, compression is
      // the real fix — the upload will keep dropping on a flaky connection
      // until the file is small enough to finish in one good window.
      let advice;
      if (status === 503) {
        // iter82/iter89: 503 means object storage was degraded for the full
        // 20-retry budget (~15 min). Most outages clear in 5-10 min, so a
        // 15-min exhaustion strongly suggests a real platform-level outage.
        // Direct the user at the resume banner AND at Emergent support.
        advice = '\n\nOur object storage has been unavailable for the past ~15 minutes. ' +
          'This is usually a brief platform-side outage that clears on its own.\n\n' +
          'What to do:\n' +
          '1. Wait 30 minutes and look for the orange "INCOMPLETE UPLOAD WAITING" banner at the top of the match page — pick the SAME file there to resume from where you left off (chunks you already delivered are durable).\n' +
          '2. If this keeps happening, email support@emergent.sh with subject "Object storage 500 errors" and reference your app domain so the platform team can investigate.';
      } else if (file.size > 2 * 1024 ** 3) {
        advice = `\n\nThis is a ${fileSizeGB} GB file. The fastest fix is to compress it BEFORE re-uploading:\n` +
          `1. Open the file in HandBrake (handbrake.fr — free)\n` +
          `2. Preset: "Fast 720p30"\n` +
          `3. Video → Constant Quality = 28\n` +
          `4. Save the smaller file (~80% smaller, same visual quality for AI analysis)\n` +
          `5. Re-upload the compressed file instead.`;
      } else {
        advice = '\n\nLook for the orange "INCOMPLETE UPLOAD WAITING" banner above and re-pick the SAME file to resume from where you left off.';
      }
      alert(`Upload interrupted: ${reason}${advice}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      alert('Please select a valid video file');
      return;
    }
    const fileSizeGB = file.size / (1024 * 1024 * 1024);
    // iter80: Always use the chunked upload path. The "standard" single-POST
    // path had zero retry logic — a single connection blip mid-upload would
    // fail the entire upload (real production bug 2026-05-21: an 845 MB
    // compressed file failed at 24% twice on the same coach's connection).
    // Chunked upload gives us per-chunk retries, offline auto-pause, and
    // resume-from-where-we-left-off for free on every video size.
    if (fileSizeGB > 5) {
      // >5 GB: user already cleared the in-page nudge in UploadPanel; upload directly.
      handleChunkedUpload(file);
    } else if (fileSizeGB > 1) {
      // 1-5 GB band: OS confirm dialog to set expectations on upload time.
      const mins = Math.max(5, Math.round(fileSizeGB * 4));
      const ok = window.confirm(
        `Large file detected (${fileSizeGB.toFixed(2)} GB).\n\n` +
        `We'll upload in resumable chunks — keep this tab open. ` +
        `On a typical home network this takes ~${mins} minutes. ` +
        `If your connection drops, the upload will resume from where it left off.\n\nContinue?`
      );
      if (!ok) return;
      handleChunkedUpload(file);
    } else {
      // ≤1 GB: still chunked — small files also benefit from retries and
      // offline auto-pause. The standard single-POST path was the source of
      // every "Video upload failed" generic alert with no diagnostic info.
      handleChunkedUpload(file);
    }
  };

  const handleReprocessVideo = async () => {
    if (!match?.video_id) return;
    try {
      await axios.post(`${API}/videos/${match.video_id}/reprocess`, {}, { headers: getAuthHeader() });
      setVideoMeta((prev) => ({ ...(prev || {}), processing_status: 'queued', processing_progress: 0 }));
    } catch (err) {
      alert('Failed to retry processing: ' + (err.response?.data?.detail || err.message));
    }
  };

  const playerGroups = useMemo(() => {
    const homeTeamPlayers = players.filter(p => p.team === match?.team_home);
    const awayTeamPlayers = players.filter(p => p.team === match?.team_away);
    const otherPlayers = players.filter(p => p.team !== match?.team_home && p.team !== match?.team_away);
    return [
      { label: match?.team_home, players: homeTeamPlayers, color: '#007AFF' },
      { label: match?.team_away, players: awayTeamPlayers, color: '#EF4444' },
      ...(otherPlayers.length > 0 ? [{ label: 'Other', players: otherPlayers, color: '#A3A3A3' }] : []),
    ];
  }, [players, match?.team_home, match?.team_away]);

  if (!match) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Spinner size={48} className="text-[#007AFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-50 bg-[#0A0A0A] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button data-testid="back-to-dashboard-btn" onClick={() => navigate('/')}
            className="p-2 hover:bg-[#1F1F1F] transition-colors border border-white/10">
            <ArrowLeft size={24} className="text-white" />
          </button>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Bebas Neue' }}>Match Details</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {pendingUploads.length > 0 && !uploading && (
          <div data-testid="pending-upload-banner"
            className="mb-6 bg-[#1A0F0A] border border-[#FBBF24]/40 px-6 py-4">
            <div className="flex items-start gap-4">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FBBF24" strokeWidth="2" className="flex-shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <div className="flex-1">
                <p className="text-sm font-bold text-[#FBBF24] tracking-wider uppercase">Incomplete upload waiting</p>
                <p className="text-xs text-[#A3A3A3] mt-1">
                  <strong className="text-white">{pendingUploads[0].filename}</strong>{' '}
                  ({pendingUploads[0].file_size_gb} GB) — {pendingUploads[0].chunks_received} of {pendingUploads[0].total_chunks} chunks delivered ({pendingUploads[0].progress_pct}%).
                </p>
                <p className="text-xs text-[#A3A3A3] mt-1">
                  Pick the <em>same</em> file below to resume from where you left off. No re-upload from scratch needed.
                </p>
              </div>
            </div>
          </div>
        )}
        {!match.video_id && (
          <ManualResultForm match={match} players={players} onSaved={() => fetchMatch()} />
        )}

        <div className="bg-[#141414] border border-white/10 p-8 mb-6">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div className="min-w-0">
              <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-2">{match.competition || 'Friendly'}</p>
              <h2 className="text-5xl font-bold mb-2" style={{ fontFamily: 'Bebas Neue' }}>
                {match.team_home} vs {match.team_away}
              </h2>
              <p className="text-[#A3A3A3]">{new Date(match.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <button
              data-testid="delete-match-detail-btn"
              onClick={async () => {
                const confirmMsg = `Delete match "${match.team_home} vs ${match.team_away}"? `
                  + (match.video_id
                    ? 'The video enters the 24h restore window. Clips and AI analyses are removed permanently.'
                    : 'This cannot be undone.');
                if (!window.confirm(confirmMsg)) return;
                try {
                  await axios.delete(`${API}/matches/${matchId}`, { headers: getAuthHeader() });
                  navigate('/');
                } catch (err) {
                  alert('Failed to delete match: ' + (err.response?.data?.detail || err.message));
                }
              }}
              className="flex-shrink-0 flex items-center gap-2 border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444] px-4 py-2 text-xs font-bold tracking-wider uppercase transition-colors"
              aria-label="Delete this match">
              <Trash size={14} weight="bold" /> Delete Match
            </button>
          </div>

          <UploadPanel match={match} matchId={matchId} videoMeta={videoMeta}
            uploading={uploading} uploadProgress={uploadProgress} uploadStatus={uploadStatus}
            onVideoUpload={handleVideoUpload}
            onShowDeleted={() => { fetchDeletedVideos(); setShowDeletedDrawer(true); }}
            onConfirmReupload={() => setConfirmReupload(true)}
            notifyOnComplete={notifyOnComplete} onToggleNotify={handleToggleNotify}
            navigate={navigate} />
        </div>

        <ProcessingProgressBar videoMeta={videoMeta} onRetry={handleReprocessVideo} />

        {match.video_id && <HighlightReelsPanel matchId={matchId} />}

        <DeletedVideosDrawer open={showDeletedDrawer} deletedVideos={deletedVideos}
          onClose={() => setShowDeletedDrawer(false)} onRestore={handleRestoreVideo} />

        <ConfirmReuploadModal open={confirmReupload} deleting={deleting}
          onConfirm={handleDeleteVideo} onCancel={() => setConfirmReupload(false)} />

        <RosterSection
          match={match} players={players} teams={teams} playerGroups={playerGroups}
          showAddPlayer={showAddPlayer} setShowAddPlayer={setShowAddPlayer}
          showCsvImport={showCsvImport} setShowCsvImport={setShowCsvImport}
          showImportTeam={showImportTeam} setShowImportTeam={setShowImportTeam}
          playerForm={playerForm} setPlayerForm={setPlayerForm}
          csvData={csvData} setCsvData={setCsvData}
          csvTeam={csvTeam} setCsvTeam={setCsvTeam}
          selectedTeamId={selectedTeamId} setSelectedTeamId={setSelectedTeamId}
          importingTeam={importingTeam}
          lastTeam={lastTeam}
          onAddPlayer={handleAddPlayer} onCsvImport={handleCsvImport}
          onFileChange={handleFileUpload} onDeletePlayer={handleDeletePlayer}
          onImportTeam={handleImportTeam} onQuickAttach={handleQuickAttach} />
      </main>
    </div>
  );
};

export default MatchDetail;
